import React, { useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Image } from 'react-native';
import { COMMUNITY_RESOURCE_ICONS } from '@/assets/admin/CommunityResourceIcons';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { onColor } from '@/constants/shared/theme/onColor';
import { MarketplaceListing } from '@/types/residents/marketplaceListing';

const BookmarkFilledIcon = COMMUNITY_RESOURCE_ICONS.bookmarkFilledIcon;
const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;

interface ListingCardProps {
  listing: MarketplaceListing;
  size: number;
  onPress: () => void;
  showBookmark?: boolean;
}

export const ListingCard: React.FC<ListingCardProps> = ({ 
  listing, 
  size, 
  onPress,
  showBookmark = true,
}) => {
  const mc = useMarketplaceColors();

  const styles = useMemo(() => StyleSheet.create({
    container: {
      borderRadius: 14,
      borderWidth: 0.45,
      borderColor: mc.borderGray,
      overflow: 'hidden',
      position: 'relative',
    },
    image: {
      width: '100%',
      height: '100%',
      borderRadius: 14,
    },
    placeholderContainer: {
      width: '100%',
      height: '100%',
      backgroundColor: mc.uploadAreaBackground,
      alignItems: 'center',
      justifyContent: 'center',
    },
    bookmarkBadge: {
      position: 'absolute',
      top: 10,
      right: 10,
      width: 34,
      height: 34,
      borderRadius: 8,
      backgroundColor: mc.accentRed,
      alignItems: 'center',
      justifyContent: 'center',
    },
    priceBadge: {
      position: 'absolute',
      bottom: 10,
      right: 10,
      backgroundColor: mc.priceBadgeBg,
      borderRadius: 30,
      paddingHorizontal: 9,
      paddingVertical: 6,
    },
    priceText: {
      fontSize: 14,
      fontWeight: '600',
      // The price badge fill is a fixed dark scrim in BOTH themes
      // (rgba(0,0,0,0.55)), so a themed ink would flip to near-black in light
      // and become unreadable on it. Derive the ink from the fill instead.
      color: onColor(mc.priceBadgeBg),
      lineHeight: 21,
    },
  }), [mc]);

  const firstImageUrl = listing.images?.[0]?.url;
  const hasImage = !!firstImageUrl;

  return (
    <TouchableOpacity 
      style={[styles.container, { width: size, height: size }]}
      onPress={onPress}
      activeOpacity={0.8}
    >
      {hasImage ? (
        <Image
          source={{ uri: firstImageUrl }}
          style={styles.image}
          resizeMode="cover"
        />
      ) : (
        <View style={styles.placeholderContainer}>
          <PropertyPlaceholder width={size * 0.8} height={size * 0.8} />
        </View>
      )}
      
      {/* Bookmark badge for saved items */}
      {showBookmark && listing.isBookmarked && (
        <View style={styles.bookmarkBadge}>
          <BookmarkFilledIcon width={20} height={20} color={mc.textPrimary} />
        </View>
      )}
      
      {/* Price badge */}
      <View style={styles.priceBadge}>
        <Text style={styles.priceText}>{listing.price}</Text>
      </View>
    </TouchableOpacity>
  );
};

